import { useEffect, useState } from 'react';
import { api, getMonitoringSnapshots, getSystemHealth, triggerMonitoringCheck, clearMonitoringSnapshots } from '../../lib/api';

interface Snapshot {
  id: string;
  tenant_id: string;
  is_reachable: boolean;
  response_time_ms: number;
  storage_used_bytes: number;
  page_count: number;
  album_count: number;
  checked_at: string;
  error_message: string | null;
}

interface SystemHealth {
  api: boolean;
  db: boolean;
  storage: boolean;
  dbBackup?: { ok: boolean; lastBackup: string | null; ageHours: number };
  details: Record<string, { ok: boolean; responseTimeMs: number }>;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL');
}

export default function Monitoring() {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [page, setPage] = useState(0);
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const PAGE_SIZE = 20;

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const [snapshotsRes, healthRes, tenantsRes] = await Promise.all([
        getMonitoringSnapshots(),
        getSystemHealth(),
        api.get<any[]>('/tenants'),
      ]);
      setSnapshots((snapshotsRes as any).snapshots ?? []);
      setHealth((healthRes as any).health ?? null);
      const names: Record<string, string> = {};
      for (const t of tenantsRes) {
        names[t.id] = t.name || t.slug || t.id;
      }
      setTenantNames(names);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function tenantLabel(id: string): string {
    return tenantNames[id] || id;
  }

  async function handleCheck() {
    setChecking(true);
    setError(null);
    setCheckResult(null);
    try {
      const result = await triggerMonitoringCheck();
      setCheckResult(`Check voltooid: ${result.succeeded} OK, ${result.failed} gefaald van ${result.total} tenants`);
      await fetchData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  }

  // Group snapshots by tenant (latest per tenant)
  const latestByTenant = new Map<string, Snapshot>();
  for (const s of snapshots) {
    if (!latestByTenant.has(s.tenant_id)) {
      latestByTenant.set(s.tenant_id, s);
    }
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Monitoring laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Monitoring</h1>
        <div className="flex items-center gap-2">
          {snapshots.length > 0 && !confirmClear && (
            <button onClick={() => setConfirmClear(true)} className="btn-secondary text-sm text-red-600 hover:bg-red-50">
              Statuschecks wissen
            </button>
          )}
          {confirmClear && (
            <>
              <span className="text-sm text-red-600">Weet je het zeker?</span>
              <button
                onClick={async () => { setClearing(true); try { await clearMonitoringSnapshots(); setSnapshots([]); setPage(0); setCheckResult('Statuschecks gewist.'); } catch {} finally { setClearing(false); setConfirmClear(false); } }}
                disabled={clearing}
                className="btn-secondary text-sm text-red-700 bg-red-50 hover:bg-red-100"
              >
                {clearing ? 'Wissen...' : 'Ja, wissen'}
              </button>
              <button onClick={() => setConfirmClear(false)} className="btn-secondary text-sm">Annuleren</button>
            </>
          )}
          <button onClick={handleCheck} disabled={checking} className="btn-primary">
            {checking ? 'Controleren...' : 'Nu controleren'}
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>}
      {checkResult && <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{checkResult}</div>}

      {/* System Health */}
      {health && (
        <div className="card">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700">Appwrite Systeemstatus</h2>
          </div>
          <div className="p-4 grid grid-cols-4 gap-4">
            {(['api', 'db', 'storage'] as const).map(service => (
              <div key={service} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <div className={`h-3 w-3 rounded-full ${health[service] ? 'bg-green-500' : 'bg-red-500'}`} />
                <div>
                  <div className="text-sm font-medium text-gray-900 capitalize">{service}</div>
                  <div className="text-xs text-gray-500">
                    {health.details[service]?.responseTimeMs >= 0
                      ? `${health.details[service].responseTimeMs}ms`
                      : 'onbereikbaar'}
                  </div>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <div className={`h-3 w-3 rounded-full ${health.dbBackup?.ok ? 'bg-green-500' : health.dbBackup?.lastBackup ? 'bg-amber-500' : 'bg-gray-300'}`} />
              <div>
                <div className="text-sm font-medium text-gray-900">DB Backup</div>
                <div className="text-xs text-gray-500">
                  {health.dbBackup?.lastBackup
                    ? `${health.dbBackup.ageHours}u geleden`
                    : 'geen data'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Status Overview */}
      <div className="card">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Tenant Status Overzicht</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responstijd</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opslag</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pagina's</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Albums</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Laatst gecontroleerd</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[...latestByTenant.values()].map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{tenantLabel(s.tenant_id)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                      s.is_reachable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${s.is_reachable ? 'bg-green-500' : 'bg-red-500'}`} />
                      {s.is_reachable ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {s.response_time_ms > 0 ? `${s.response_time_ms}ms` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(s.storage_used_bytes)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.page_count >= 0 ? s.page_count : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.album_count >= 0 ? s.album_count : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(s.checked_at)}</td>
                </tr>
              ))}
              {latestByTenant.size === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">Nog geen monitoring data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Snapshots */}
      <div className="card">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Recente statuschecks ({snapshots.length})</h2>
          {snapshots.length > PAGE_SIZE && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Vorige
              </button>
              <span className="text-xs text-gray-500">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, snapshots.length)} van {snapshots.length}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= snapshots.length}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Volgende
              </button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tenant</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tijd</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Fout</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {snapshots.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(s => (
                <tr key={s.id} className="text-sm">
                  <td className="px-4 py-2 text-gray-700">{tenantLabel(s.tenant_id)}</td>
                  <td className="px-4 py-2">
                    <span className={`h-2 w-2 rounded-full inline-block ${s.is_reachable ? 'bg-green-500' : 'bg-red-500'}`} />
                  </td>
                  <td className="px-4 py-2 text-gray-500">{formatDate(s.checked_at)}</td>
                  <td className="px-4 py-2 text-red-500 text-xs truncate max-w-xs">{s.error_message ?? ''}</td>
                </tr>
              ))}
              {snapshots.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 text-sm">Nog geen statuschecks</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
